import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { AuthService, usuarioTienePrivilegio } from 'app/core/auth/auth.service';
import { map, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * Gating de UX adicional a AuthGuard: oculta la navegación directa por URL cuando el usuario
 * tiene un Permiso asignado que no incluye el privilegio requerido. No reemplaza la validación
 * real, que ocurre en el backend vía @priv.requerir en cada endpoint.
 *
 * `accionClave` acepta un arreglo para rutas de formulario que sirven tanto alta como edición
 * (ej. CREAR o EDITAR): basta con que el usuario tenga alguna de las acciones indicadas.
 */
export const canActivatePermiso = (moduloClave: string, accionClave: string | string[]): CanActivateFn => () =>
  inject(AuthService).getUser().pipe(
    map(user => {
      const acciones = Array.isArray(accionClave) ? accionClave : [accionClave];
      return acciones.some(accion => usuarioTienePrivilegio(user, moduloClave, accion));
    }),
    catchError(() => of(true)),
  );

/**
 * Variante id-aware para rutas de formulario que sirven tanto alta como edición con la misma ruta
 * (ej. /formusuario para crear y /formusuario?id=X para editar): exige CREAR cuando no hay query
 * param `id`, EDITAR cuando sí lo hay. A diferencia de `canActivatePermiso` con un arreglo de
 * acciones (que acepta cualquiera de las dos indistintamente), esta distingue el modo real de la
 * navegación para que un usuario con solo EDITAR no pueda llegar al modo de alta por URL directa.
 */
export const canActivatePermisoFormulario = (moduloClave: string): CanActivateFn => (route) =>
  inject(AuthService).getUser().pipe(
    map(user => {
      const tieneId = !!route.queryParamMap.get('id');
      const accionRequerida = tieneId ? 'EDITAR' : 'CREAR';
      return usuarioTienePrivilegio(user, moduloClave, accionRequerida);
    }),
    catchError(() => of(true)),
  );
